// DOC-DEPS: LLM.md -> docs/CURRENT_TASK.md -> src/features/report/README.md
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { format, isSameDay } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { buildRootSegments, renderRootSegments } from '../../../lib/rootRenderer';
import { mapSourcesToPlantActivities, toPlantCategoryKey } from '../../../lib/plantActivityMapper';
import { useChatStore } from '../../../store/useChatStore';
import { useAuthStore } from '../../../store/useAuthStore';
import { useReportStore } from '../../../store/useReportStore';
import { usePlantStore, resolvePlantDurationForMessage } from '../../../store/usePlantStore';
import { buildPlantGenerateUiState } from './plantGenerateUi';
import { playLoopSound, stopSound } from '../../../services/sound/soundService';
import { dismissKeyboard } from '../../../services/native/keyboardService';
import { canEditDailyMyDiary } from '../../../lib/reportDiaryEditPolicy';
import type { PlantCategoryKey } from '../../../types/plant';
import { PlantFlipCard } from './PlantFlipCard';
import { SoilCanvas } from './SoilCanvas';
import { DayEcoSphere } from './DayEcoSphere';
import {
  APP_GREEN_GLASS_BUTTON_STYLE,
  APP_GREEN_GLASS_TEXT,
  APP_MODAL_CARD_CLASS,
  APP_MODAL_OVERLAY_CLASS,
  APP_MODAL_PRIMARY_BUTTON_CLASS,
  APP_MODAL_SECONDARY_BUTTON_CLASS,
} from '../../../lib/modalTheme';

const SOIL_CANVAS_TOP_OFFSET_PX = 106;

function getCategoryKey(category: PlantCategoryKey): string {
  switch (category) {
    case 'work_study':
      return 'plant_category_work_study';
    case 'exercise':
      return 'plant_category_exercise';
    case 'social':
      return 'category_social';
    case 'entertainment':
      return 'category_entertainment';
    default:
      return 'category_life';
  }
}

interface PlantRootSectionProps {
  autoGeneratePlantToken?: number;
  onDiaryDraftChange?: (text: string) => void;
  onOpenTodayDiary?: () => Promise<void> | void;
}

export const PlantRootSection: React.FC<PlantRootSectionProps> = ({
  autoGeneratePlantToken,
  onDiaryDraftChange,
  onOpenTodayDiary,
}) => {
  const { t } = useTranslation();
  const reports = useReportStore(state => state.reports);
  const isPlus = useAuthStore(state => state.isPlus);
  const updateReport = useReportStore(state => state.updateReport);
  const generateReport = useReportStore(state => state.generateReport);
  const generateAIDiary = useReportStore(state => state.generateAIDiary);
  const todaySegments = usePlantStore(state => state.todaySegments);
  const todayPlant = usePlantStore(state => state.todayPlant);
  const isPlantGenerating = usePlantStore(state => state.isGenerating);
  const generatePlant = usePlantStore(state => state.generatePlant);
  const ensureTodayRootSnapshot = usePlantStore(state => state.ensureTodayRootSnapshot);
  const selectedRootId = usePlantStore(state => state.selectedRootId);
  const directionOrder = usePlantStore(state => state.directionOrder);
  const loadTodayData = usePlantStore(state => state.loadTodayData);
  const refreshTodaySegments = usePlantStore(state => state.refreshTodaySegments);
  const startActivitySync = usePlantStore(state => state.startActivitySync);
  const stopActivitySync = usePlantStore(state => state.stopActivitySync);
  const setSelectedRootId = usePlantStore(state => state.setSelectedRootId);
  const messages = useChatStore(state => state.messages);
  const loadMessagesForDateRange = useChatStore(state => state.loadMessagesForDateRange);
  const [myDiaryText, setMyDiaryText] = useState('');
  const [isDiaryEditing, setIsDiaryEditing] = useState(false);
  const [isDiarySaving, setIsDiarySaving] = useState(false);
  const [plantStatusHint, setPlantStatusHint] = useState<string | null>(null);
  const [diaryStatusHint, setDiaryStatusHint] = useState<string | null>(null);
  const [isDiaryGenerating, setIsDiaryGenerating] = useState(false);
  const [showEarlyCard, setShowEarlyCard] = useState(false);
  const [showGenerateConfirm, setShowGenerateConfirm] = useState(false);
  const activeDiaryReportIdRef = useRef<string | null>(null);
  const pendingDiaryReportIdRef = useRef<string | null>(null);
  const isClosingDiaryEditorRef = useRef(false);
  const plantActionsRef = useRef<HTMLDivElement | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const diaryTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const diarySaveButtonRef = useRef<HTMLButtonElement | null>(null);

  const resizeDiaryTextarea = useCallback((target?: HTMLTextAreaElement | null) => {
    const el = target ?? diaryTextareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.max(128, el.scrollHeight)}px`;
  }, []);

  const ensureDiaryEditorAboveKeyboard = useCallback(() => {
    if (!isDiaryEditing) return;
    const scrollEl = scrollContainerRef.current;
    const textEl = diaryTextareaRef.current;
    if (!scrollEl || !textEl) return;

    const rootStyle = window.getComputedStyle(document.documentElement);
    const keyboardHeight = Number.parseFloat(rootStyle.getPropertyValue('--keyboard-height') || '0') || 0;
    const safeBottom = window.innerHeight - keyboardHeight - 12;

    const textRect = textEl.getBoundingClientRect();
    const saveRect = diarySaveButtonRef.current?.getBoundingClientRect() ?? null;
    const blockedBottom = Math.max(textRect.bottom, saveRect?.bottom ?? textRect.bottom);
    const overflow = blockedBottom - safeBottom;
    if (overflow > 0) {
      scrollEl.scrollTop += overflow + 8;
    }
  }, [isDiaryEditing]);

  const todayDailyReport = useMemo(
    () => reports.find((report) => report.type === 'daily' && isSameDay(new Date(report.date), new Date())) ?? null,
    [reports],
  );
  const isMyDiaryEditable = todayDailyReport
    ? canEditDailyMyDiary(todayDailyReport.date)
    : true;

  useEffect(() => {
    const reportId = todayDailyReport?.id ?? null;
    if (activeDiaryReportIdRef.current === reportId) return;
    activeDiaryReportIdRef.current = reportId;
    if (reportId) pendingDiaryReportIdRef.current = reportId;
    const reportNote = todayDailyReport?.userNote ?? '';
    setMyDiaryText((prev) => {
      if (prev.trim().length > 0 && reportNote.trim().length === 0) return prev;
      return reportNote;
    });
    setIsDiaryEditing(false);
    setIsDiarySaving(false);
  }, [todayDailyReport?.id, todayDailyReport?.userNote]);

  useEffect(() => {
    onDiaryDraftChange?.(myDiaryText);
  }, [myDiaryText, onDiaryDraftChange]);

  useEffect(() => {
    resizeDiaryTextarea();
  }, [myDiaryText, resizeDiaryTextarea]);

  useEffect(() => {
    if (!isDiaryEditing) return;
    const timerId = window.setTimeout(() => {
      ensureDiaryEditorAboveKeyboard();
    }, 140);
    return () => window.clearTimeout(timerId);
  }, [ensureDiaryEditorAboveKeyboard, isDiaryEditing, myDiaryText]);

  const persistDiaryNote = useCallback(async () => {
    if (!isMyDiaryEditable) return;
    const nextNote = myDiaryText;
    const nextTrimmed = nextNote.trim();
    let reportId = todayDailyReport?.id ?? pendingDiaryReportIdRef.current;

    if (!reportId) {
      if (!nextTrimmed) return;
      reportId = await generateReport('daily', Date.now());
      pendingDiaryReportIdRef.current = reportId;
    }

    const latest = useReportStore.getState().reports.find(report => report.id === reportId);
    if ((latest?.userNote ?? '') === nextNote) return;
    await updateReport(reportId, { userNote: nextNote });
  }, [generateReport, isMyDiaryEditable, myDiaryText, todayDailyReport?.id, updateReport]);

  useEffect(() => {
    if (!isDiaryEditing) return;
    const timerId = window.setTimeout(() => {
      void persistDiaryNote();
    }, 600);

    return () => window.clearTimeout(timerId);
  }, [isDiaryEditing, persistDiaryNote]);

  const handleDiarySave = useCallback(async () => {
    if (!isMyDiaryEditable) return;
    setIsDiarySaving(true);
    try {
      await persistDiaryNote();
    } finally {
      isClosingDiaryEditorRef.current = true;
      setIsDiarySaving(false);
      setIsDiaryEditing(false);
      window.setTimeout(() => {
        void dismissKeyboard(diaryTextareaRef.current);
        isClosingDiaryEditorRef.current = false;
      }, 0);
    }
  }, [isMyDiaryEditable, persistDiaryNote]);

  useEffect(() => {
    if (isMyDiaryEditable) return;
    setIsDiaryEditing(false);
    setIsDiarySaving(false);
  }, [isMyDiaryEditable]);

  useEffect(() => {
    void (async () => {
      await loadTodayData();
      const now = new Date();
      const dayStart = new Date(now);
      dayStart.setHours(0, 0, 0, 0);
      const dayEnd = new Date(now);
      dayEnd.setHours(23, 59, 59, 999);
      await loadMessagesForDateRange(dayStart, dayEnd);
      refreshTodaySegments();
    })();
    startActivitySync();
    const timerId = window.setInterval(() => {
      refreshTodaySegments();
    }, 30_000);
    return () => {
      window.clearInterval(timerId);
      stopActivitySync();
    };
  }, [loadMessagesForDateRange, loadTodayData, refreshTodaySegments, startActivitySync, stopActivitySync]);

  const renderedSegments = useMemo(() => renderRootSegments(todaySegments), [todaySegments]);

  // When plant is locked, the store clears todaySegments (refreshTodaySegments returns early).
  // Recompute from messages so the flip card back can display the root system.
  const flipCardSegments = useMemo(() => {
    if (todayPlant?.rootSnapshot) return todayPlant.rootSnapshot.segments;
    if (todaySegments.length > 0) return todaySegments;
    if (!todayPlant) return [];
    const now = new Date();
    const dayStart = new Date(now); dayStart.setHours(0, 0, 0, 0);
    const dayStartMs = dayStart.getTime();
    const dayEndMs = dayStartMs + 86_400_000;
    const nowMs = Date.now();
    const sources = messages
      .filter(m => m.mode === 'record' && !m.isMood && m.timestamp >= dayStartMs && m.timestamp < dayEndMs)
      .map(m => ({ ...m, duration: resolvePlantDurationForMessage(m.duration, m.timestamp, nowMs) }));
    const activities = mapSourcesToPlantActivities(sources);
    const dirMap: Record<PlantCategoryKey, 0 | 1 | 2 | 3 | 4> = {
      entertainment: 0, social: 1, work_study: 2, exercise: 3, life: 4,
    };
    directionOrder.forEach((cat, idx) => { dirMap[cat] = idx as 0 | 1 | 2 | 3 | 4; });
    return buildRootSegments(
      activities.map(a => ({ activityId: a.id, direction: dirMap[a.categoryKey], minutes: a.minutes, focus: a.focus })),
      `plant-${todayPlant.date}`,
    );
  }, [todayPlant, todaySegments, messages, directionOrder]);
  const flipCardDirectionOrder = todayPlant?.rootSnapshot?.directionOrder ?? directionOrder;

  useEffect(() => {
    if (!todayPlant || todayPlant.rootSnapshot) return;
    void ensureTodayRootSnapshot();
  }, [ensureTodayRootSnapshot, todayPlant]);

  const messageMap = useMemo(() => {
    const map = new Map<string, {
      content: string;
      category: PlantCategoryKey;
      timestamp: number;
    }>();
    messages.forEach((message) => {
      map.set(message.id, {
        content: message.content,
        category: toPlantCategoryKey(message.activityType, message.content),
        timestamp: message.timestamp,
      });
    });
    return map;
  }, [messages]);

  const selectedSegment = useMemo(
    () => todaySegments.find(segment => segment.id === selectedRootId) ?? null,
    [selectedRootId, todaySegments],
  );

  const selectedMessage = selectedSegment ? messageMap.get(selectedSegment.activityId) : null;

  const todayActivityCount = useMemo(() => {
    const now = new Date();
    const dayStartMs = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const dayEndMs = dayStartMs + 86_400_000;
    return messages.filter(m => m.mode === 'record' && !m.isMood && m.timestamp >= dayStartMs && m.timestamp < dayEndMs).length;
  }, [messages]);

  useEffect(() => {
    if (!selectedRootId) return;
    const timerId = window.setTimeout(() => {
      setSelectedRootId(null);
    }, 5000);
    return () => window.clearTimeout(timerId);
  }, [selectedRootId, setSelectedRootId]);

  const selectedTimeRange = useMemo(() => {
    if (!selectedSegment || !selectedMessage) {
      return '-';
    }
    const startTs = selectedMessage.timestamp;
    const endTs = startTs + selectedSegment.minutes * 60 * 1000;
    return `${format(startTs, 'HH:mm')}-${format(endTs, 'HH:mm')}`;
  }, [selectedMessage, selectedSegment]);

  const plantIsTooEarly = new Date().getHours() < 20;
  const diaryAlreadyGenerated = useMemo(() => {
    if (!todayDailyReport) return false;
    const fullText = todayDailyReport.aiAnalysis?.trim();
    const teaserText = todayDailyReport.teaserText?.trim();
    return isPlus ? Boolean(fullText) : Boolean(teaserText);
  }, [isPlus, todayDailyReport]);
  const canGenerateDiary = !plantIsTooEarly && !diaryAlreadyGenerated && !isDiaryGenerating;
  const plantGenerateUi = buildPlantGenerateUiState({
    hasTodayPlant: Boolean(todayPlant),
    isGenerating: isPlantGenerating,
    isTooEarly: plantIsTooEarly,
  });

  const executeGeneratePlant = useCallback(async () => {
    playLoopSound('plantGrow');
    try {
      const response = await generatePlant();
      stopSound('plantGrow');
      if (response.status === 'generated') { setPlantStatusHint(t('plant_generate_success')); return; }
      if (response.status === 'already_generated') { setPlantStatusHint(t('plant_generate_already')); return; }
      if (response.status === 'empty_day') { setPlantStatusHint(t('plant_generate_empty_day_fallback')); return; }
      if (response.status === 'monthly_exhausted') { setPlantStatusHint(t('plant_generate_monthly_exhausted')); return; }
      setPlantStatusHint(response.message ?? t('plant_generate_locked_hint'));
    } catch {
      stopSound('plantGrow');
      setPlantStatusHint(t('plant_generate_failed'));
    }
  }, [generatePlant, t]);

  const handleGeneratePlant = useCallback(() => {
    if (plantIsTooEarly) {
      setShowEarlyCard(prev => !prev);
      return;
    }
    setShowGenerateConfirm(true);
  }, [plantIsTooEarly]);

  const handleGenerateDiary = useCallback(async () => {
    if (diaryAlreadyGenerated) {
      setDiaryStatusHint(t('report_generate_already'));
      return;
    }
    if (new Date().getHours() < 20) {
      setDiaryStatusHint(t('report_early_tip'));
      return;
    }

    setDiaryStatusHint(null);
    setIsDiaryGenerating(true);
    try {
      const today = new Date();
      let reportId = todayDailyReport?.id;
      if (!reportId) {
        reportId = await generateReport('daily', today.getTime());
      }
      await generateAIDiary(reportId);
      const updatedReport = useReportStore.getState().reports.find(r => r.id === reportId);
      if (updatedReport?.analysisStatus === 'error') {
        setDiaryStatusHint(t('plant_generate_failed'));
      } else {
        setDiaryStatusHint(t('report_generate_success'));
      }
    } catch {
      setDiaryStatusHint(t('plant_generate_failed'));
    } finally {
      setIsDiaryGenerating(false);
    }
  }, [diaryAlreadyGenerated, generateAIDiary, generateReport, t, todayDailyReport?.id]);

  const handleOpenDiaryFromPlant = useCallback(() => {
    if (onOpenTodayDiary) {
      void Promise.resolve(onOpenTodayDiary());
      return;
    }
    void handleGenerateDiary();
  }, [handleGenerateDiary, onOpenTodayDiary]);

  useEffect(() => {
    if (!autoGeneratePlantToken) return;
    void handleGeneratePlant();
  }, [autoGeneratePlantToken, handleGeneratePlant]);

  useEffect(() => {
    if (!plantStatusHint) return;
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (!target || plantActionsRef.current?.contains(target)) return;
      setPlantStatusHint(null);
    };
    document.addEventListener('pointerdown', handlePointerDown, true);
    return () => document.removeEventListener('pointerdown', handlePointerDown, true);
  }, [plantStatusHint]);

  /* ── When plant is generated: show flip card only ── */
  if (todayPlant) {
    return (
      <div className="h-full flex flex-col relative overflow-hidden">
        <PlantFlipCard
          plant={todayPlant}
          segments={flipCardSegments}
          directionOrder={flipCardDirectionOrder}
          onGenerateDiary={handleOpenDiaryFromPlant}
          isGeneratingDiary={isDiaryGenerating}
          isDiaryButtonDisabled={!canGenerateDiary}
          diaryButtonHint={diaryStatusHint}
        />
      </div>
    );
  }

  return (
    /* Keep root visualization full-size; diary area is reachable by scrolling down. */
    <div ref={scrollContainerRef} className="app-scroll-container h-full relative">

      {/* Reserve first-view space for the plant action and My Diary heading. */}
      <div
        className="relative overflow-hidden"
        style={{ height: 'clamp(320px, calc(100% - 120px), 540px)' }}
      >
        {/* Soil + roots: slightly taller soil image while keeping eco-sphere bubbles above it. */}
        <div
          className="absolute inset-x-4 bottom-0"
          style={{ top: SOIL_CANVAS_TOP_OFFSET_PX }}
        >
          <SoilCanvas
            items={renderedSegments}
            selectedRootId={selectedRootId}
            onSelectRoot={setSelectedRootId}
            directionOrder={directionOrder}
            detailBubble={selectedSegment ? {
              title: t('plant_detail_title'),
              activity: `${t('plant_detail_activity')}: ${selectedMessage?.content ?? '-'}`,
              category: `${t('plant_detail_category')}: ${t(getCategoryKey(selectedMessage?.category ?? 'life'))}`,
              timeRange: `${t('plant_detail_time_range')}: ${selectedTimeRange}`,
              duration: `${t('plant_detail_duration')}: ${t('duration_minutes', { mins: selectedSegment.minutes })}`,
              focus: `${t('plant_detail_focus')}: ${selectedSegment.focus === 'high' ? t('plant_focus_high') : t('plant_focus_medium')}`,
            } : null}
            onCloseDetail={() => setSelectedRootId(null)}
          />
        </div>

        {/* ── Eco sphere bubbles above soil ── */}
        <div className="absolute top-0 left-0 right-0 z-30 pointer-events-none">
          <DayEcoSphere />
        </div>
        {/* Empty state hint (centered in canvas) */}
        {renderedSegments.length === 0 ? (
          <div className="absolute inset-x-8 top-[52%] -translate-y-1/2 z-10 pointer-events-none">
            <div className="rounded-2xl px-4 py-3 text-center" style={{ background: 'transparent', border: 'none' }}>
              <p className="app-caption" style={{ color: '#5a4028' }}>{t('report_root_empty_title')}</p>
              <p className="app-description mt-1 font-medium" style={{ color: '#5a4028' }}>{t('report_root_empty')}</p>
            </div>
          </div>
        ) : null}
      </div>

      {/* ── Plant generate button ── */}
      <div ref={plantActionsRef} className="flex flex-col items-center gap-2 py-3">
        <button
          onClick={handleGeneratePlant}
          disabled={plantGenerateUi.disabled}
          className="app-body rounded-full px-5 py-1.5 font-medium transition active:opacity-70 disabled:opacity-55 disabled:cursor-not-allowed"
          style={{ ...APP_GREEN_GLASS_BUTTON_STYLE, color: APP_GREEN_GLASS_TEXT }}
        >
          {t(plantGenerateUi.buttonKey)}
        </button>
        {plantStatusHint ? (
          <p className="app-badge pointer-events-none text-center" style={{ color: '#5f6f65' }}>
            {plantStatusHint}
          </p>
        ) : null}
        {showEarlyCard && (
          <div
            className="mx-4 w-[calc(100%-2rem)] max-w-xs rounded-2xl animate-in fade-in slide-in-from-bottom-2 duration-200"
            style={{
              background: 'rgba(255,255,255,0.28)',
              backdropFilter: 'blur(20px) saturate(140%)',
              WebkitBackdropFilter: 'blur(20px) saturate(140%)',
              border: '1px solid rgba(255,255,255,0.55)',
              boxShadow: '0 0 12px rgba(255,255,255,0.20), inset 0 1px 1px rgba(255,255,255,0.55)',
            }}
          >
            <div className="px-5 py-4 text-center">
              <p className="app-body font-semibold" style={{ color: '#2d3f5a' }}>
                {t('plant_generate_locked_hint')}
              </p>
              {todayActivityCount > 0 && (
                <p className="app-description mt-2" style={{ color: '#7a9b80' }}>
                  {t('plant_early_activities', { count: todayActivityCount })}
                </p>
              )}
              <button
                onClick={() => setShowEarlyCard(false)}
                className="app-caption mt-3 rounded-full px-5 py-1 font-semibold transition active:opacity-70"
                style={{ ...APP_GREEN_GLASS_BUTTON_STYLE, color: APP_GREEN_GLASS_TEXT }}
              >
                {t('report_early_tip_ok')}
              </button>
            </div>
          </div>
        )}
      </div>

      {showGenerateConfirm ? (
        <div
          className={`fixed inset-0 z-50 flex items-center justify-center p-6 ${APP_MODAL_OVERLAY_CLASS}`}
          onClick={() => setShowGenerateConfirm(false)}
        >
          <div
            className={`${APP_MODAL_CARD_CLASS} w-full max-w-xs rounded-3xl p-6 text-center`}
            onClick={(event) => event.stopPropagation()}
          >
            <p className="app-body text-slate-700">{t('plant_generate_confirm')}</p>
            <div className="mt-5 flex gap-3">
              <button
                type="button"
                className={`${APP_MODAL_SECONDARY_BUTTON_CLASS} app-body flex-1 py-2.5`}
                onClick={() => setShowGenerateConfirm(false)}
              >
                {t('cancel')}
              </button>
              <button
                type="button"
                className={`${APP_MODAL_PRIMARY_BUTTON_CLASS} app-body flex-1 py-2.5`}
                onClick={() => {
                  setShowGenerateConfirm(false);
                  void executeGeneratePlant();
                }}
              >
                {t('confirm')}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <div
        className="px-4 pt-2"
        style={{
          paddingBottom: isDiaryEditing
            ? 'calc(env(safe-area-inset-bottom,0px) + 92px + var(--keyboard-height, 0px))'
            : 'calc(env(safe-area-inset-bottom,0px) + 92px)',
        }}
      >
        <h3 className="app-body font-bold" style={{ color: '#334155' }}>{t('report_my_diary')}</h3>
        <div className="mt-2">
          <textarea
            ref={diaryTextareaRef}
            value={myDiaryText}
            onChange={(event) => {
              if (!isMyDiaryEditable) return;
              setMyDiaryText(event.target.value);
              resizeDiaryTextarea(event.currentTarget);
              window.setTimeout(() => ensureDiaryEditorAboveKeyboard(), 0);
            }}
            onFocus={() => {
              if (!isMyDiaryEditable) return;
              setIsDiaryEditing(true);
              window.setTimeout(() => ensureDiaryEditorAboveKeyboard(), 140);
            }}
            onBlur={() => {
              if (!isMyDiaryEditable) return;
              if (isClosingDiaryEditorRef.current) return;
              if (!isDiaryEditing) return;
              void persistDiaryNote();
              setIsDiaryEditing(false);
            }}
            readOnly={!isMyDiaryEditable}
            placeholder={t('report_diary_placeholder')}
            className="app-form-text w-full resize-none border-0 border-b border-slate-300/60 bg-transparent px-0 py-1 leading-6 outline-none transition focus:border-[#8FAF92]"
            style={{ minHeight: 128, color: isMyDiaryEditable ? '#334155' : '#94A3B8', overflowY: 'hidden' }}
          />
          {isDiaryEditing && isMyDiaryEditable ? (
            <div className="mt-2 flex justify-end">
              <button
                ref={diarySaveButtonRef}
                type="button"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => { void handleDiarySave(); }}
                disabled={isDiarySaving}
                className="app-caption rounded-full px-3 py-1 font-semibold disabled:opacity-70"
                style={{ ...APP_GREEN_GLASS_BUTTON_STYLE, color: APP_GREEN_GLASS_TEXT }}
              >
                {isDiarySaving ? `${t('report_save')}...` : t('report_save')}
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};
